import html2canvas, { type Options as Html2CanvasOptions } from "html2canvas";
import { useCallback, useState } from "react";

type UseScreenshotOptions = {
  type?: string;
  quality?: number;
};

type ScreenshotErrorState = {
  error: unknown;
};

type TakeScreenshot = (
  node: HTMLElement,
  options?: Partial<Html2CanvasOptions>,
) => Promise<string | undefined>;

const waitForImages = async (node: HTMLElement): Promise<void> => {
  const images = Array.from(node.querySelectorAll("img"));

  await Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          const finish = () => {
            image.removeEventListener("load", finish);
            image.removeEventListener("error", finish);
            resolve();
          };

          image.addEventListener("load", finish, { once: true });
          image.addEventListener("error", finish, { once: true });
        }),
    ),
  );
};

export const useScreenshot = (
  initialOptions: UseScreenshotOptions = {},
): [string | null, TakeScreenshot, ScreenshotErrorState] => {
  const { type, quality } = initialOptions;
  const [image, setImage] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const takeScreenshot = useCallback<TakeScreenshot>(
    async (node, options = {}) => {
      if (!node) {
        throw new Error("You should provide correct html node.");
      }

      try {
        await waitForImages(node);

        const canvas = await html2canvas(node, {
          backgroundColor: null,
          logging: false,
          useCORS: true,
          ...options,
        });
        const croppedCanvas = document.createElement("canvas");
        const croppedCanvasContext = croppedCanvas.getContext("2d");

        if (!croppedCanvasContext) {
          throw new Error("Unable to create canvas context.");
        }

        croppedCanvas.width = canvas.width;
        croppedCanvas.height = canvas.height;
        croppedCanvasContext.drawImage(canvas, 0, 0);

        const base64Image = croppedCanvas.toDataURL(type, quality);
        setImage(base64Image);
        setError(null);
        return base64Image;
      } catch (nextError) {
        setError(nextError);
        return undefined;
      }
    },
    [quality, type],
  );

  return [image, takeScreenshot, { error }];
};
